import Layout from '@/components/feature/Layout';
import SEOHead from '@/components/base/SEOHead';
import { localBusinessSchema, buildBreadcrumbSchema, buildFaqSchema, buildServiceSchema, buildGraphSchema } from '@/components/base/schemas';
import { Link } from 'react-router-dom';
import { useState } from 'react';

const serviceSchema = buildServiceSchema(
  'Réparation de volets roulants à Paris 15ᵉ',
  'Volet roulant bloqué, sangle cassée, moteur HS dans le 15ᵉ ? Réparation toutes marques, manuel & motorisé.'
);

const breadcrumbSchema = buildBreadcrumbSchema([
  { name: 'Accueil', item: 'https://fenetre-paris15.fr/' },
  { name: 'Volets roulants', item: 'https://fenetre-paris15.fr/reparation-volet-roulant-paris-15/' },
]);

const faqs = [
  { q: `Mon volet roulant est bloqué en position basse. Que faire ?`, a: `Ne forcez surtout pas sur la manivelle ou le moteur, vous risqueriez d'aggraver la panne. Coupez l'alimentation si c'est un volet motorisé. Le blocage peut venir des lames désolidarisées, d'un axe tordu, ou d'un obstacle dans les coulisses. DF PRO intervient rapidement pour diagnostiquer et réparer.` },
  { q: `Réparez-vous les volets roulants motorisés Somfy ?`, a: `Oui, nous maîtrisons les motorisations Somfy, Bubendorff, Profalux, et la plupart des grandes marques. Nous pouvons remplacer un moteur défectueux, reprogrammer une télécommande ou changer le condensateur.` },
  { q: `Combien coûte le changement d'une sangle de volet roulant ?`, a: `Le remplacement d'une sangle (y compris l'enrouleur si nécessaire) coûte généralement entre 90€ et 180€ selon le type de sangle et l'accessibilité du coffre. Un devis précis vous sera remis après diagnostic.` },
];

export default function VoletPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <Layout>
      <SEOHead
        title="Réparation Volet Roulant Paris 15 (75015) — 24/48h | DF PRO"
        description="Volet roulant bloqué, sangle cassée, moteur HS dans le 15ᵉ ? Réparation toutes marques, manuel & motorisé. Devis gratuit. ☎ 01 87 66 81 55"
        canonicalUrl="https://fenetre-paris15.fr/reparation-volet-roulant-paris-15/"
        schema={buildGraphSchema(localBusinessSchema, breadcrumbSchema, buildFaqSchema(faqs), serviceSchema)}
      />

      <div className="bg-brand-mint/30 py-3">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <nav aria-label="Fil d'Ariane" className="text-xs text-brand-text/60">
            <Link to="/" className="hover:text-brand-accent">Accueil</Link><span className="mx-2">/</span><span className="text-brand-accent font-medium">Volets roulants</span>
          </nav>
        </div>
      </div>

      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
            <div>
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-brand-dark leading-tight mb-4">Réparation de volets roulants à Paris 15ᵉ</h1>
              <p className="text-brand-text/70 text-base leading-relaxed mb-6">
                Votre volet roulant est <strong>bloqué en haut ou en bas</strong>, la sangle est cassée, le moteur ne répond plus ou la manivelle tourne dans le vide ? DF PRO est votre spécialiste volet roulant dans le 15ᵉ. Nous réparons tous les types de volets, manuels comme motorisés, toutes marques.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/devis-fenetre-paris-15/" className="btn-primary text-sm">Devis gratuit</Link>
                <a href="tel:+33187668155" className="btn-outline text-sm"><i className="ri-phone-line"></i> 01 87 66 81 55</a>
              </div>
            </div>
            <div className="aspect-[4/3] rounded-2xl overflow-hidden">
              <img src="/images/dfpro-volet-hero.webp" alt="Volet roulant sur façade parisienne — DF PRO réparation Paris 15" className="w-full h-full object-cover" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-20 bg-brand-mint/40">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="section-title mb-8">Pannes fréquentes des volets roulants</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {[
              { icon: 'ri-arrow-up-down-line', title: 'Volet bloqué', desc: `Le tablier ne monte ni ne descend. Causes possibles : lames sorties des coulisses, axe tordu, obstacle dans le coffre, ou moteur en sécurité.` },
              { icon: 'ri-link-unlink', title: 'Sangle cassée', desc: `La sangle s'effiloche ou casse net. L'enrouleur peut aussi être usé. Remplacement de la sangle et du mécanisme si nécessaire.` },
              { icon: 'ri-refresh-line', title: `Manivelle tourne dans le vide`, desc: `Le cardan est désengagé ou cassé. Le treuil ne transmet plus le mouvement. Pièce à remplacer, souvent en une seule visite.` },
              { icon: 'ri-layout-2-line', title: `Lames désolidarisées`, desc: `Les lames se séparent, le tablier s'effondre partiellement. Remise en place et remplacement des attaches si besoin.` },
              { icon: 'ri-cpu-line', title: 'Moteur HS', desc: `Le moteur ne répond plus, fait un bruit anormal, ou s'arrête en cours de course. Diagnostic électrique et remplacement moteur.` },
              { icon: 'ri-remote-control-line', title: `Télécommande / récepteur`, desc: `Le volet ne répond plus à la télécommande. Reprogrammation ou remplacement du récepteur radio.` },
            ].map((item, idx) => (
              <div key={idx} className="bg-white rounded-xl p-5 md:p-6 card-hover">
                <div className="w-10 h-10 rounded-xl bg-brand-mint text-brand-accent flex items-center justify-center mb-4">
                  <i className={`${item.icon} text-lg`}></i>
                </div>
                <h3 className="font-heading font-bold text-brand-dark text-base mb-2">{item.title}</h3>
                <p className="text-brand-text/60 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
            <div>
              <h2 className="section-title mb-4">Manuel ou motorisé : nous réparons tout</h2>
              <p className="text-brand-text/70 leading-relaxed mb-4">
                Les volets roulants manuels (sangle ou manivelle) et motorisés (Somfy, Bubendorff, Profalux, etc.) ne se réparent pas de la même façon. Nos techniciens maîtrisent les deux technologies et disposent d'un stock de pièces couvrant l'essentiel des pannes courantes.
              </p>
              <ul className="space-y-2 text-brand-text/70 text-sm">
                <li className="flex items-start gap-2"><i className="ri-check-line text-brand-accent mt-0.5"></i> <strong>Volets manuels :</strong> sangle, enrouleur, treuil, cardan, butées, attaches de lames</li>
                <li className="flex items-start gap-2"><i className="ri-check-line text-brand-accent mt-0.5"></i> <strong>Volets motorisés :</strong> moteur tubulaire, condensateur, récepteur radio, télécommande, fins de course</li>
                <li className="flex items-start gap-2"><i className="ri-check-line text-brand-accent mt-0.5"></i> <strong>Parties communes :</strong> tablier, lames, coulisses, axe, coffre</li>
              </ul>
            </div>
            <div className="bg-gradient-to-br from-brand-gradientFrom to-brand-gradientTo rounded-xl p-6 md:p-8 text-white">
              <h2 className="font-heading font-bold text-xl mb-4">Urgence volet roulant</h2>
              <p className="text-white/80 text-sm leading-relaxed mb-4">
                Un volet bloqué en position ouverte la nuit est un <strong>problème de sécurité</strong>. Un volet bloqué fermé le matin vous prive de lumière. Dans les deux cas, DF PRO traite ces situations en priorité. Nous nous engageons à intervenir sous 24 à 48h, et faisons notre maximum pour les urgences le jour même.
              </p>
              <p className="text-white/80 text-sm leading-relaxed mb-6">
                N'attendez pas que la panne s'aggrave. Un volet qui force peut endommager le moteur, casser la sangle ou déformer les lames.
              </p>
              <a href="tel:+33187668155" className="inline-flex items-center gap-2 bg-white text-brand-dark font-semibold rounded-full px-6 py-3 text-sm hover:opacity-90 transition-opacity whitespace-nowrap">
                <i className="ri-phone-line"></i> Appeler : 01 87 66 81 55
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-20 bg-brand-mint/40">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <h2 className="section-title mb-8 text-center">Questions fréquentes — Volets roulants</h2>
          <div className="space-y-3">
            {faqs.map((faq, idx) => (
              <div key={idx} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-brand-mint/30 transition-colors cursor-pointer">
                  <span className="font-heading font-bold text-brand-dark text-sm pr-4">{faq.q}</span>
                  <i className={`ri-${openFaq === idx ? 'subtract' : 'add'}-line text-brand-accent text-lg flex-shrink-0`}></i>
                </button>
                {openFaq === idx && <div className="px-5 pb-4"><p className="text-brand-text/70 text-sm">{faq.a}</p></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-white text-center">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <h2 className="section-title mb-4">Volet roulant bloqué ?</h2>
          <p className="section-subtitle mx-auto mb-6">DF PRO intervient rapidement dans le 15ᵉ pour réparer votre volet roulant</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-8">
            <Link to="/devis-fenetre-paris-15/" className="btn-primary text-sm">Devis gratuit</Link>
            <a href="tel:+33187668155" className="btn-outline text-sm"><i className="ri-phone-line"></i> 01 87 66 81 55</a>
          </div>
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            <Link to="/motorisation-volet-roulant-paris-15/" className="text-brand-accent hover:underline">Motorisation de volets</Link>
            <span className="text-brand-text/30">|</span>
            <Link to="/blog/volet-roulant-bloque-causes-solutions/" className="text-brand-accent hover:underline">Article : volet roulant bloqué</Link>
            <span className="text-brand-text/30">|</span>
            <Link to="/zones-intervention-paris-15/" className="text-brand-accent hover:underline">Zones d'intervention</Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}